import React, { useState } from 'react';
import {
  Pagination,
  Button,
  Form,
  Input,
  Spin,
  Select,
  DatePicker,
} from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { PlusOutlined, DeleteFilled } from '@ant-design/icons';
import CustomModal from '../../../components/common/CustomModal';
import SortableTableHeader from '../../../components/common/SortableTableHeader';
import ReactSwitch from 'react-switch';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getSport,
  removeSport,
  addSport,
  API_URL,
} from '../../../store/slices/adminSlice';
import { useTableState, useModal, useDeleteConfirm, useDataLoading } from '../../../hooks';
import { requiredRule } from '../../../utils';
import moment, { Moment } from 'moment';
import toastr from 'toastr';
import axios from 'axios';
import * as constant from '../../../actions/constant';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// TypeScript interfaces
interface SportType {
  id: number;
  name: string;
}

interface BonusType {
  id: number;
  name: string;
}

interface SportData {
  _id: string;
  sportname: string;
  description: string;
  type: string;
  startDate: string;
  endDate: string;
  stack: string;
  bonus: boolean;
  isActive: boolean | null;
  isInviteCompButton?: boolean;
}

interface SportFormValues {
  sportname: string;
  description: string;
  type: string;
  date: [Moment, Moment];
  Stackvalue: string;
  bonus: string;
}

interface MessageFormValues {
  messageTitle: string;
  messageDescription: string;
}

const Sport: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<SportFormValues>();
  const [messageForm] = Form.useForm<MessageFormValues>();

  // Redux state
  const sportData = useAppSelector((state) => state.admin.sportData);
  const totalCount = useAppSelector((state) => state.admin.sportCount);

  // Use custom hooks for common patterns
  const { currentPage, sortOrder, changePage, sortData, refreshData } = useTableState({
    fetchAction: getSport,
  });

  const { visible, itemId: sportId, showModal, hideModal } = useModal({ form });

  const { showDeleteConfirm } = useDeleteConfirm({
    deleteAction: removeSport,
    idParamName: 'sportId',
  });

  const [loading] = useDataLoading(sportData);

  // Additional local state for sport-specific features
  const [isSportUpdate, setIsSportUpdate] = useState<boolean>(false);
  const [message, setMessage] = useState<boolean>(false);
  const [selectedSport, setSelectedSport] = useState<SportData | null>(null);

  const sportTypeData: SportType[] = [
    { id: 1, name: 'Regular' },
    { id: 2, name: 'Draw' },
  ];

  const BonusData: BonusType[] = [
    { id: 1, name: 'true' },
    { id: 2, name: 'false' },
  ];

  const handleShowModal = (
    id: string,
    name: string,
    description: string,
    type?: string,
    startDate?: string,
    endDate?: string,
    stack?: string,
    bonus?: boolean
  ) => {
    showModal(id);

    if (id !== '') {
      form.resetFields();
      form.setFieldsValue({
        sportname: name,
        description: description,
        type: type,
        date: [moment(new Date(startDate!)), moment(new Date(endDate!))],
        Stackvalue: stack,
        bonus: `${bonus}`,
      });
    } else {
      setIsSportUpdate(false);
      form.resetFields();
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const obj = {
        sportname: values.sportname,
        description: values.description,
        sportId: sportId,
        type: values.type,
        startDate: values.date[0].toISOString(),
        endDate: values.date[1].toISOString(),
        stack: values.Stackvalue,
        bonus: values.bonus === 'true',
      };

      setIsSportUpdate(false);

      const endDate = new Date(values.date[1].toISOString());
      const current = new Date();

      if (current.getTime() <= endDate.getTime()) {
        hideModal();
        dispatch(addSport(obj));
      } else {
        toastr.warning('The End Date should be longer than the Current Date');
      }
    } catch (error) {
      // Validation failed - form will show errors
    }
  };

  const handleMenuClick = (value: string) => {
    const obj = {
      seasonId: value,
      page: (currentPage || 0) as number,
    };
    if (obj.seasonId === '') {
      refreshData();
    } else {
      dispatch(getSport(obj));
    }
  };

  const showModalMessage = (sport: SportData, isMultiple: boolean) => {
    setMessage(true);
    setSelectedSport(sport);
    messageForm.setFieldsValue({
      messageTitle: '',
      messageDescription: '',
    });
  };

  const handleCancelMessage = () => {
    setMessage(false);
    setSelectedSport(null);
  };

  const handleSendMessage = async () => {
    try {
      const values = await messageForm.validateFields();
      const obj = {
        title: values.messageTitle,
        message: values.messageDescription,
        sportId: selectedSport?._id,
      };

      if (values.messageTitle === '' || values.messageDescription === '') {
        toastr.warning('Please Enter Data');
      } else {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('access_token') || '',
        };
        await axios.post(API_URL + 'message/sendSportMessage', obj, { headers });
        toastr.success('Message sent successfully');
        setMessage(false);
        setSelectedSport(null);
      }
    } catch (error) {
      // Validation failed - form will show errors
    }
  };

  const handleChange = async (sport: SportData) => {
    const params = {
      sportId: sport._id,
      isInviteCompButton: sport.isInviteCompButton ? !sport.isInviteCompButton : true,
    };
    const headers = {
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem('access_token') || '',
    };
    await axios.post(API_URL + constant.setButton, params, { headers });
    refreshData();
  };

  const sportRows = (sportData as SportData[]).map((x: SportData) => (
    <tr key={x._id}>
      <td>{x.sportname}</td>
      <td>{x.startDate}</td>
      <td>{x.endDate}</td>
      <td>{x.type}</td>
      <td>{x.description}</td>
      <td>
        {x.isActive == null ? '' : x.isActive === true ? 'Open' : 'Closed'}
      </td>
      <td className="action-icons">
        <Button
          className="btn-sm mr-2"
          type="link"
          onClick={() =>
            handleShowModal(
              x._id,
              x.sportname,
              x.description,
              x.type,
              x.startDate,
              x.endDate,
              x.stack,
              x.bonus
            )
          }
        >
          <i className="fa fa-pencil" style={{ color: '#666' }}></i>
        </Button>
        <Button
          className="btn-sm mr-2"
          type="link"
          onClick={() => showDeleteConfirm(x._id)}
        >
          <DeleteFilled style={{ color: '#666' }} />
        </Button>
        <Button
          className="btn-sm mr-2"
          type="link"
          onClick={() => showModalMessage(x, false)}
        >
          <i className="fa fa-paper-plane" style={{ color: '#666' }} aria-hidden="true"></i>
        </Button>
      </td>
      <td>
        <ReactSwitch
          checked={x?.isInviteCompButton || false}
          onChange={() => handleChange(x)}
        />
      </td>
    </tr>
  ));

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader
          prefixedClassName="ant-page-header"
          title="Sport"
          subTitle=""
          extra={[
            <Button
              key="1"
              type="default"
              onClick={() => handleShowModal('', '', '')}
            >
              <PlusOutlined className="mr-1" />
              <span> Add Sport</span>
            </Button>,
          ]}
        />
        <Spin spinning={loading} delay={500}>
          <div className="box-body">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <SortableTableHeader
                      label="Sport Name"
                      sortKey="sportname"
                      currentSortOrder={sortOrder}
                      onSort={sortData}
                    />
                    <th scope="col">Start Date</th>
                    <th scope="col">End Date</th>
                    <th scope="col">Type</th>
                    <th scope="col">Description</th>
                    <th scope="col">Status</th>
                    <th scope="col">Action</th>
                    <th scope="col">Invite&Comp Button</th>
                  </tr>
                </thead>
                <tbody>{sportRows}</tbody>
              </table>
            </div>
            <Pagination
              style={{ textAlign: 'right' }}
              pageSize={10}
              defaultCurrent={currentPage}
              total={totalCount ? totalCount : 1}
              onChange={changePage}
            />
            <CustomModal
              handleCancel={hideModal}
              handleOk={handleOk}
              visible={visible}
              title="Sport"
              backBtnText="Back"
              submitBtnText="Submit"
            >
              <Form form={form} className="sport-form l_hgt0" layout="vertical">
                <Form.Item
                  label="Sport Name"
                  name="sportname"
                  rules={[requiredRule('sport')]}
                >
                  <Input placeholder="Sport" />
                </Form.Item>
                <Form.Item
                  label="Description"
                  name="description"
                  rules={[requiredRule('description')]}
                >
                  <Input placeholder="Description" />
                </Form.Item>
                <Form.Item
                  label="Select start date and end date"
                  name="date"
                  rules={[{ required: true, message: 'Please enter date range' }]}
                >
                  <RangePicker disabled={isSportUpdate} />
                </Form.Item>
                <Form.Item
                  label="Sport Type"
                  name="type"
                  rules={[requiredRule('sport type')]}
                >
                  <Select disabled={isSportUpdate} placeholder="Sport Type" onChange={handleMenuClick}>
                    {sportTypeData.map((x, index) => (
                      <Option value={x.name} key={index}>
                        {x.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Round Bonus"
                  name="bonus"
                  rules={[requiredRule('Bonus type')]}
                >
                  <Select placeholder="Bonus">
                    {BonusData.map((x, index) => (
                      <Option value={x.name} key={index}>
                        {`${x.name}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="MULTI CALCULATOR STAKE VALUE"
                  name="Stackvalue"
                  rules={[requiredRule('Stack value')]}
                >
                  <Input placeholder="stack Value" />
                </Form.Item>
              </Form>
            </CustomModal>

            <CustomModal
              handleCancel={handleCancelMessage}
              handleOk={handleSendMessage}
              visible={message}
              title={selectedSport?.sportname || ''}
              backBtnText="Back"
              submitBtnText="Submit"
            >
              <Form form={messageForm} className="team-form l_hgt0" layout="vertical">
                <Form.Item
                  label="Message Title"
                  name="messageTitle"
                  rules={[requiredRule('title')]}
                >
                  <Input placeholder="Title" />
                </Form.Item>
                <Form.Item
                  label="Message Description"
                  name="messageDescription"
                  rules={[requiredRule('description')]}
                >
                  <TextArea placeholder="Description" />
                </Form.Item>
              </Form>
            </CustomModal>
          </div>
        </Spin>
      </div>
    </>
  );
};

export default Sport;
